import Decimal from "decimal.js";

import { D0, D1, D2, D4, D20, DPI } from "../constants";
import { ORIGIN, transform2DVectors, VectorD3 } from "../vector";
import { Box, WCollider } from "./collider";

export class WCircleCollider extends WCollider {

  public localPoints: VectorD3[];
  public points: VectorD3[];

  constructor(
    public radius: Decimal,
    location = ORIGIN
  ) {
    super(location, D0);

    const subdivisions = D20;
    const stepAngle = new Decimal(2).mul(DPI).div(subdivisions);

    this.localPoints = [];
    this.points = [];
    for(let i = D0; i.lessThan(subdivisions); i = i.add(D1)) {
      const angle = stepAngle.mul(i);
      const localX = this.radius.mul(angle.cos());
      const localY = this.radius.mul(angle.sin());
      this.localPoints.push(new VectorD3(localX, localY, D0));
      const x = this.location.x.add(localX);
      const y = this.location.y.add(localY);
      this.points.push(new VectorD3(x, y, this.location.z));
    }
  }

  // Misc

  clone(): WCollider {
    return new WCircleCollider(this.radius, this.location);
  }

  // Position

  setLocationAndRotation(location: VectorD3, _rotation: Decimal) {
    this.location.copy(location);
    this.rotation = _rotation;
    this.updatePoints();
  }

  setLocation(location: VectorD3) {
    this.location.copy(location);
    this.updatePoints();
  }

  setRotation(_rotation: Decimal) { this.rotation = _rotation; }

  rotate(_angle: Decimal) { this.rotation = this.rotation.plus(_angle); }

  updatePoints() {
    this.points = transform2DVectors(this.localPoints, this.location, D0);
  }

  // Collisions

  contains(collider: WCollider): boolean {
    if(collider instanceof WCircleCollider) {
      if(collider.radius.greaterThan(this.radius)) return false;

      const dr = this.radius.minus(collider.radius);

      return this.location.distanceToSquared(collider.location).lessThanOrEqualTo(dr.mul(dr));
    }

    return collider.isContained(this);
  }

  isContained(collider: WCollider): boolean {
    return collider.contains(this);
  }

  overlaps(collider: WCollider): boolean {
    if(collider instanceof WCircleCollider) {
      const sr = this.radius.plus(collider.radius);

      return this.location.distanceToSquared(collider.location).lessThan(sr.mul(sr));
    }

    return collider.overlaps(this);
  }

  intersectsSegment(p1: VectorD3, p2: VectorD3) {
    const dx = p2.x.minus(p1.x);
    const dy = p2.y.minus(p1.y);

    const fx = p1.x.minus(this.location.x);
    const fy = p1.y.minus(this.location.y);

    const a = dx.mul(dx).plus(dy.mul(dy));
    const b = D2.mul(fx.mul(dx).plus(fy.mul(dy)));
    const c = fx.mul(fx).plus(fy.mul(fy)).minus(this.radius.mul(this.radius));

    const discriminant = b.mul(b).minus(D4.mul(a).mul(c));

    // No intersections
    if(discriminant.lessThanOrEqualTo(D0)) return false;

    const sqrtDiscriminant = discriminant.squareRoot();

    const t1 = sqrtDiscriminant.negated().minus(b).div(D2.mul(a));
    const t2 = sqrtDiscriminant.minus(b).div(D2.mul(a));

    // Check if at least one intersection is inside the segment
    return (t1.greaterThan(D0) && t1.lessThan(D1)) || (t2.greaterThan(D0) && t2.lessThan(D1));
  }

  createExpanded(distance: Decimal): WCollider {
    return new WCircleCollider(this.radius.add(distance), this.location);
  }

  getBox() {
    const left = this.location.x.sub(this.radius);
    const right = this.location.x.add(this.radius);
    const top = this.location.y.sub(this.radius);
    const bottom = this.location.y.add(this.radius);
    return new Box(left, right, top, bottom);
  }

  getLocalPoints(): VectorD3[] {
    return this.localPoints;
  }
  // TODO: This is a hack to get the points of the circle. It should be replaced with a more efficient method.
  getPoints(): VectorD3[] {
    return this.points;
  }

  distanceTo(collider: WCollider): Decimal {
    if(! (collider instanceof WCircleCollider)) return D0;

    return VectorD3.sub(this.location, collider.location).length().sub(this.radius).sub(collider.radius);
  }
}
